export type ChatSharedEntityType =
  | 'tenant'
  | 'tenantAccount'
  | 'program'
  | 'onboarding'
  | 'user'
  | 'property'
  | 'tariff'

export interface ChatSharedEntity {
  type: ChatSharedEntityType
  id: string
}

export interface ChatPageContext {
  pathname: string
  search: Record<string, string>
  params: Record<string, string>
  area: string
  label: string
  sharedEntity?: ChatSharedEntity
}

const TAB_LABELS: Record<string, string> = {
  general: 'General',
  invoices: 'Invoices',
  payments: 'Payments',
  'billing-events': 'Billing events',
  'billing-options': 'Billing options',
  documents: 'Documents',
  'service-agreements': 'Service agreements',
  'account-managers': 'Account managers',
  'incentive-plans': 'Incentive plans',
}

function searchToRecord(search: string): Record<string, string> {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
  const out: Record<string, string> = {}
  params.forEach((value, key) => {
    out[key] = value
  })
  return out
}

function matchPath(
  pathname: string,
  pattern: string
): { matched: boolean; params: Record<string, string> } {
  const pathParts = pathname.split('/').filter(Boolean)
  const patternParts = pattern.split('/').filter(Boolean)
  if (pathParts.length !== patternParts.length) {
    return { matched: false, params: {} }
  }
  const params: Record<string, string> = {}
  for (let i = 0; i < patternParts.length; i++) {
    const part = patternParts[i]!
    const value = pathParts[i]!
    if (part.startsWith(':')) {
      params[part.slice(1)] = decodeURIComponent(value)
    } else if (part !== value) {
      return { matched: false, params: {} }
    }
  }
  return { matched: true, params }
}

/**
 * Map the current admin route to a chat pageContext snapshot.
 * Unknown routes still send pathname/search/params so Sage knows the URL.
 */
export function buildPageContext(pathname: string, search = ''): ChatPageContext {
  const searchMap = searchToRecord(search)

  const tryMatch = (
    pattern: string,
    build: (params: Record<string, string>) => Omit<ChatPageContext, 'pathname' | 'search' | 'params'>
  ): ChatPageContext | null => {
    const { matched, params } = matchPath(pathname, pattern)
    if (!matched) return null
    const built = build(params)
    return {
      pathname,
      search: searchMap,
      params,
      ...built,
    }
  }

  const edit = tryMatch('/tenant/tenants/:tenantId/edit', (params) => ({
    area: 'tenant',
    label: `Edit tenant ${params.tenantId}`,
    sharedEntity: { type: 'tenant', id: params.tenantId! },
  }))
  if (edit) return edit

  const tenantDetail = tryMatch('/tenant/tenants/:tenantId', (params) => {
    const san = searchMap.serviceAccountNumber
    const tab = searchMap.tab
    const tabLabel = tab ? TAB_LABELS[tab] ?? tab : undefined
    if (san) {
      return {
        area: 'tenant',
        label: tabLabel ? `${san} · ${tabLabel}` : san,
        sharedEntity: { type: 'tenantAccount', id: san },
      }
    }
    return {
      area: 'tenant',
      label: `Tenant ${params.tenantId}`,
      sharedEntity: { type: 'tenant', id: params.tenantId! },
    }
  })
  if (tenantDetail) return tenantDetail

  const onboardingDetail = tryMatch('/tenant/onboarding/:registrationId', (params) => ({
    area: 'onboarding',
    label: `Onboarding ${params.registrationId}`,
    sharedEntity: { type: 'onboarding', id: params.registrationId! },
  }))
  if (onboardingDetail) return onboardingDetail

  const reconciliation = tryMatch('/program/programs/:programId/reconciliation', (params) => ({
    area: 'program',
    label: `Program ${params.programId} · Reconciliation`,
    sharedEntity: { type: 'program', id: params.programId! },
  }))
  if (reconciliation) return reconciliation

  const programDetail = tryMatch('/program/programs/:programId', (params) => ({
    area: 'program',
    label: `Program ${params.programId}`,
    sharedEntity: { type: 'program', id: params.programId! },
  }))
  if (programDetail) return programDetail

  if (pathname === '/admin/tariffs/rate-validation') {
    return { pathname, search: searchMap, params: {}, area: 'tariff', label: 'Rate validation' }
  }
  if (pathname === '/admin/tariffs/acc-rates') {
    return { pathname, search: searchMap, params: {}, area: 'tariff', label: 'NEM3 export rates' }
  }

  const tariffDetail = tryMatch('/admin/tariffs/:tariffId', (params) => ({
    area: 'tariff',
    label: `Tariff ${params.tariffId}`,
    sharedEntity: { type: 'tariff', id: params.tariffId! },
  }))
  if (tariffDetail) return tariffDetail

  const userDetail = tryMatch('/users/:userID', (params) => ({
    area: 'users',
    label: `User ${params.userID}`,
    sharedEntity: { type: 'user', id: params.userID! },
  }))
  if (userDetail) return userDetail

  if (pathname === '/' || pathname === '') {
    return { pathname: '/', search: searchMap, params: {}, area: 'system', label: 'System' }
  }
  if (pathname === '/tenant/tenants') {
    return { pathname, search: searchMap, params: {}, area: 'tenant', label: 'Tenants' }
  }
  if (pathname === '/tenant/onboarding') {
    return { pathname, search: searchMap, params: {}, area: 'onboarding', label: 'Onboarding' }
  }
  if (pathname === '/program/programs') {
    return { pathname, search: searchMap, params: {}, area: 'program', label: 'Programs' }
  }
  if (pathname === '/collections/invoices') {
    return { pathname, search: searchMap, params: {}, area: 'collections', label: 'Invoices' }
  }
  if (pathname === '/collections/payments') {
    return { pathname, search: searchMap, params: {}, area: 'collections', label: 'Payments' }
  }
  if (pathname === '/notes') {
    return { pathname, search: searchMap, params: {}, area: 'notes', label: 'Notes' }
  }
  if (pathname === '/users') {
    return { pathname, search: searchMap, params: {}, area: 'users', label: 'Users' }
  }
  if (pathname === '/tenant-users') {
    return { pathname, search: searchMap, params: {}, area: 'users', label: 'Tenant users' }
  }
  if (pathname === '/file-explorer') {
    return { pathname, search: searchMap, params: {}, area: 'admin', label: 'File explorer' }
  }
  if (pathname === '/admin/base-case-model') {
    return { pathname, search: searchMap, params: {}, area: 'admin', label: 'Base case model' }
  }
  if (pathname === '/admin/event-handling-report') {
    return { pathname, search: searchMap, params: {}, area: 'admin', label: 'Event handling report' }
  }
  if (pathname === '/admin/go-live-readiness') {
    return { pathname, search: searchMap, params: {}, area: 'admin', label: 'Go-live readiness' }
  }
  if (pathname === '/admin/tariffs') {
    return { pathname, search: searchMap, params: {}, area: 'tariff', label: 'Tariffs' }
  }

  if (pathname === '/saber' || pathname.startsWith('/saber/')) {
    const screen = pathname.startsWith('/saber/') ? pathname.slice('/saber/'.length).split('/')[0] || 'dashboard' : 'dashboard'
    const property = searchMap.property
    const screenLabel =
      screen === 'dashboard'
        ? 'Dashboard'
        : screen === 'detail'
          ? 'Shadow Billing'
          : screen === 'base-case'
            ? 'Base Case Model'
            : screen === 'config'
              ? 'Engine Status'
              : screen
    return {
      pathname,
      search: searchMap,
      params: {},
      area: 'saber',
      label: property ? `SABER / ${screenLabel} · ${property}` : `SABER / ${screenLabel}`,
      sharedEntity: property ? { type: 'property', id: property } : undefined,
    }
  }

  return {
    pathname,
    search: searchMap,
    params: {},
    area: 'admin',
    label: pathname,
  }
}
